'use client';

import { useEffect, useState, useRef } from 'react';
import { ChevronDown, ArrowRight, Sparkles } from 'lucide-react';

const TITLE_LINE_1 = "L'Art du Cuir";
const TITLE_LINE_2 = 'Façonné à la Main';

const ROTATING_TAGS = [
  'Sacs à Main',
  'Ceintures',
  'Chaussures',
  'Sur-Mesure',
  'Manteaux',
  'Fourrures',
  'Nattes de Prière',
];

const STATS = [
  { value: 25, suffix: '', label: 'Années', sub: "d'expertise" },
  { value: 13, suffix: '', label: 'Collections', sub: "d'exception" },
  { value: 100, suffix: '%', label: 'Fait Main', sub: 'à Paris' },
];

function useCountUp(target: number, duration = 2000, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      // easeOutExpo
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

export default function Hero() {
  const [mounted, setMounted] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [tagIdx, setTagIdx] = useState(0);
  const [startStats, setStartStats] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const t1 = setTimeout(() => setMounted(true), 100);
    const t2 = setTimeout(() => setStartStats(true), 1800);

    const onMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2; // -1..1
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };

    const onScroll = () => setScrollY(window.scrollY);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Rotate tagline every 2.4s
  useEffect(() => {
    if (!mounted) return;
    const interval = setInterval(() => {
      setTagIdx((i) => (i + 1) % ROTATING_TAGS.length);
    }, 2400);
    return () => clearInterval(interval);
  }, [mounted]);

  const scrollToNext = () => {
    const el = document.querySelector('#maison');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Parallax intensity (max offset in px)
  const parallax = (depth: number) => ({
    transform: `translate3d(${mousePos.x * depth}px, ${mousePos.y * depth}px, 0)`,
  });

  // Scroll-based parallax (background moves slower than content)
  const bgScrollStyle = {
    transform: `translate3d(0, ${scrollY * 0.4}px, 0) scale(${1 + scrollY * 0.0003})`,
  };

  // Letter reveal helper
  const renderLetters = (text: string, baseDelay: number) =>
    text.split('').map((ch, i) => (
      <span
        key={i}
        className="letter-reveal"
        style={{ animationDelay: `${baseDelay + i * 60}ms` }}
      >
        {ch === ' ' ? '\u00A0' : ch}
      </span>
    ));

  // Hero scroll progress (0..1 over the hero section height)
  const heroProgress = Math.min(1, scrollY / (typeof window !== 'undefined' ? window.innerHeight : 800));

  return (
    <section
      ref={sectionRef}
      id="accueil"
      className="relative min-h-screen w-full overflow-hidden"
      style={{ perspective: '1200px' }}
    >
      {/* === Layer 0: Background image with scroll parallax === */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center animate-slow-zoom"
          style={{
            backgroundImage: 'url(/images/hero-main.png)',
            ...bgScrollStyle,
          }}
        />
        {/* Cinematic vignette */}
        <div className="absolute inset-0 hero-overlay" />

        {/* Atmospheric light orbs with mouse parallax */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={parallax(-20)}
        >
          <div
            className="absolute top-[20%] left-[15%] w-[400px] h-[400px] rounded-full opacity-30 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(212,160,23,0.5) 0%, transparent 70%)',
            }}
          />
          <div
            className="absolute bottom-[20%] right-[10%] w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
            style={{
              background: 'radial-gradient(circle, rgba(139,69,19,0.6) 0%, transparent 70%)',
            }}
          />
        </div>

        {/* Film grain overlay */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none animate-grain"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
            backgroundSize: '300px 300px',
          }}
        />

        {/* Vertical side text — left */}
        <div
          className={`absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-20 transition-all duration-1000 delay-1000 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C9A961]/60 to-transparent" />
          <span
            className="font-serif-display text-[#C9A961] text-[10px] tracking-[0.4em] uppercase whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Atelier · Rue Clignancourt
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C9A961]/60 to-transparent" />
        </div>

        {/* Vertical side text — right */}
        <div
          className={`absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-20 transition-all duration-1000 delay-1000 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C9A961]/60 to-transparent" />
          <span
            className="font-serif-display text-[#C9A961] text-[10px] tracking-[0.4em] uppercase whitespace-nowrap"
            style={{ writingMode: 'vertical-rl' }}
          >
            Paris · 75018 · France
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C9A961]/60 to-transparent" />
        </div>
      </div>

      {/* === Layer 1: Floating product showcase with mouse parallax === */}
      <div className="absolute inset-0 z-10 pointer-events-none hidden lg:block">
        {/* Floating handbag — top left */}
        <div
          className="absolute top-[14%] left-[8%] animate-float-product"
          style={parallax(35)}
        >
          <div className="relative w-32 h-32 xl:w-40 xl:h-40 rounded-2xl overflow-hidden border border-[#C9A961]/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-sm">
            <img
              src="/images/product-handbag.png"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/60 to-transparent" />
            <div className="absolute bottom-2 left-2 right-2">
              <span className="text-[#C9A961] text-[9px] tracking-[0.2em] uppercase font-serif-display">
                Sacs à Main
              </span>
            </div>
          </div>
        </div>

        {/* Floating boots — bottom right */}
        <div
          className="absolute bottom-[20%] right-[8%] animate-float-product-alt"
          style={parallax(45)}
        >
          <div className="relative w-32 h-32 xl:w-40 xl:h-40 rounded-2xl overflow-hidden border border-[#C9A961]/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-sm">
            <img
              src="/images/product-boots.png"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/60 to-transparent" />
            <div className="absolute bottom-2 left-2 right-2">
              <span className="text-[#C9A961] text-[9px] tracking-[0.2em] uppercase font-serif-display">
                Chaussures
              </span>
            </div>
          </div>
        </div>

        {/* Floating belt — middle right */}
        <div
          className="absolute top-[45%] right-[20%] animate-float-product"
          style={{ ...parallax(60), animationDelay: '1s' }}
        >
          <div className="relative w-28 h-28 xl:w-36 xl:h-36 rounded-2xl overflow-hidden border border-[#C9A961]/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-sm">
            <img
              src="/images/product-belt.png"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/60 to-transparent" />
            <div className="absolute bottom-2 left-2 right-2">
              <span className="text-[#C9A961] text-[9px] tracking-[0.2em] uppercase font-serif-display">
                Ceintures
              </span>
            </div>
          </div>
        </div>

        {/* Floating wallet — middle left */}
        <div
          className="absolute top-[55%] left-[14%] animate-float-product-alt"
          style={{ ...parallax(50), animationDelay: '1.5s' }}
        >
          <div className="relative w-28 h-28 xl:w-36 xl:h-36 rounded-2xl overflow-hidden border border-[#C9A961]/40 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-sm">
            <img
              src="/images/product-wallet.png"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2B1810]/60 to-transparent" />
            <div className="absolute bottom-2 left-2 right-2">
              <span className="text-[#C9A961] text-[9px] tracking-[0.2em] uppercase font-serif-display">
                Maroquinerie
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* === Layer 2: Decorative corner ornaments === */}
      <div className="absolute top-28 left-8 hidden lg:block z-10 pointer-events-none">
        <div
          className={`transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}
          style={parallax(15)}
        >
          <div className="w-px h-24 bg-gradient-to-b from-transparent to-[#C9A961]/60" />
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#C9A961]/60 mt-2" />
        </div>
      </div>
      <div className="absolute top-28 right-8 hidden lg:block z-10 pointer-events-none">
        <div
          className={`transition-all duration-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}
          style={parallax(15)}
        >
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#C9A961]/60 ml-auto" />
          <div className="w-px h-24 bg-gradient-to-b from-transparent to-[#C9A961]/60 mt-2 ml-auto" />
        </div>
      </div>

      {/* === Layer 3: Main content === */}
      <div
        className="relative z-30 min-h-screen flex flex-col items-center justify-center px-6 text-center"
        style={parallax(8)}
      >
        {/* Eyebrow with ornament */}
        <div
          className={`mb-6 md:mb-8 transition-all duration-1000 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 px-4">
            <Sparkles size={14} className="text-[#C9A961] animate-pulse" />
            <div className="w-8 md:w-12 h-px bg-[#C9A961]" />
            <span className="text-[#C9A961] font-serif-display text-[10px] sm:text-sm md:text-base tracking-[0.3em] sm:tracking-[0.5em] uppercase text-center whitespace-nowrap">
              Maison Parisienne · Depuis 1998
            </span>
            <div className="w-8 md:w-12 h-px bg-[#C9A961]" />
            <Sparkles size={14} className="text-[#C9A961] animate-pulse" />
          </div>
        </div>

        {/* Main title — letter-by-letter reveal */}
        <h1 className="font-display text-[#F8F1E4] text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] font-medium leading-[0.95] tracking-tight max-w-5xl px-2">
          <span className="inline-block">
            {renderLetters(TITLE_LINE_1, 400)}
          </span>
          <br />
          <span className="inline-block text-gold-shimmer font-semibold italic">
            {renderLetters(TITLE_LINE_2, 1100)}
          </span>
        </h1>

        {/* Animated rotating tagline */}
        <div
          className={`mt-6 md:mt-7 h-7 overflow-hidden transition-all duration-1000 delay-1500 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="flex flex-col items-center transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ transform: `translateY(-${tagIdx * 28}px)` }}
          >
            {ROTATING_TAGS.map((tag, i) => (
              <span
                key={i}
                className="font-serif-display italic text-[#C9A961] text-base md:text-lg tracking-[0.2em] uppercase h-7 flex items-center"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Subtitle */}
        <p
          className={`mt-6 md:mt-8 max-w-2xl px-4 text-[#F8F1E4]/85 text-base md:text-xl font-serif-display font-light leading-relaxed transition-all duration-1000 delay-1700 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Au cœur du 18e arrondissement de Paris, ASMAO STYLE CUIR perpétue un
          savoir-faire ancestral. Chaque pièce est une ode au cuir véritable,
          taillée, cousue et patinée par les mains expertes de nos artisans.
        </p>

        {/* CTAs */}
        <div
          className={`mt-10 md:mt-12 flex flex-col items-stretch sm:flex-row sm:items-center gap-4 sm:gap-5 px-4 w-full max-w-xl transition-all duration-1000 delay-2000 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            onClick={() => {
              const el = document.querySelector('#collections');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group relative px-8 py-4 bg-[#C9A961] text-[#2B1810] font-medium text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] uppercase overflow-hidden transition-all duration-500 hover:bg-[#D4A017] hover:shadow-[0_8px_30px_rgba(212,160,23,0.4)] rounded-sm text-center"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              Découvrir les Collections
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
          </button>
          <button
            onClick={() => {
              const el = document.querySelector('#atelier');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group px-8 py-4 border border-[#F8F1E4]/40 text-[#F8F1E4] hover:border-[#C9A961] hover:text-[#C9A961] font-medium text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] uppercase transition-all duration-500 rounded-sm backdrop-blur-sm bg-white/5 text-center flex items-center justify-center gap-2"
          >
            <span>Visiter l'Atelier</span>
            <ChevronDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
          </button>
        </div>

        {/* Stats row */}
        <div
          className={`mt-14 md:mt-16 grid grid-cols-3 gap-6 md:gap-12 transition-all duration-1000 delay-2300 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {STATS.map((stat, i) => (
            <StatCounter key={i} stat={stat} start={startStats} />
          ))}
        </div>
      </div>

      {/* === Layer 4: Scroll indicator + progress === */}
      <div
        className={`absolute bottom-28 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-30 transition-all duration-1000 delay-2500 ${
          mounted ? 'opacity-100' : 'opacity-0'
        } ${scrollY > 100 ? 'opacity-0 pointer-events-none' : ''}`}
        style={{ transition: 'opacity 0.5s' }}
      >
        <span className="text-[#C9A961]/70 text-[10px] tracking-[0.4em] uppercase font-serif-display">
          Défiler
        </span>
        <button
          onClick={scrollToNext}
          className="relative text-[#C9A961]/70 hover:text-[#C9A961] transition-colors group"
          aria-label="Défiler vers le bas"
        >
          <div className="w-7 h-12 rounded-full border-2 border-[#C9A961]/60 flex items-start justify-center p-1.5 group-hover:border-[#C9A961] transition-colors">
            <span className="w-1 h-2 rounded-full bg-[#C9A961] animate-bounce" />
          </div>
        </button>
      </div>

      {/* === Layer 5: Top scroll progress bar === */}
      <div className="fixed top-0 left-0 right-0 z-[70] h-0.5 bg-transparent pointer-events-none">
        <div
          className="h-full bg-gradient-to-r from-[#C9A961] via-[#D4A017] to-[#C9A961]"
          style={{
            width: `${(scrollY / (typeof document !== 'undefined' ? document.body.scrollHeight - window.innerHeight : 1)) * 100}%`,
            transition: 'width 0.1s ease-out',
          }}
        />
      </div>

      {/* === Layer 6: Bottom band — address signature with rotating accent === */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-[#2B1810] via-[#2B1810]/80 to-transparent pt-20 pb-6">
        <div className="container mx-auto px-6 flex flex-wrap items-center justify-center md:justify-between gap-3 md:gap-4 text-[#F8F1E4]/60 text-[10px] md:text-xs tracking-[0.2em] md:tracking-[0.25em] uppercase font-serif-display text-center">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>SMARTEST CAPITAL</span>
          </div>
          <span className="hidden md:inline">Rue Clignancourt · 75018 Paris</span>
          <span>Savoir-Faire Français</span>
        </div>
      </div>
    </section>
  );
}

function StatCounter({
  stat,
  start,
}: {
  stat: { value: number; suffix: string; label: string; sub: string };
  start: boolean;
}) {
  const value = useCountUp(stat.value, 2200, start);
  return (
    <div className="text-center">
      <div className="font-display text-3xl md:text-4xl lg:text-5xl text-[#F8F1E4] font-semibold leading-none">
        <span className="text-gold-shimmer">{value}</span>
        <span className="text-[#C9A961]">{stat.suffix}</span>
      </div>
      <div className="mt-2 text-[#F8F1E4]/80 text-xs md:text-sm tracking-[0.2em] uppercase font-serif-display">
        {stat.label}
      </div>
      <div className="text-[#C9A961]/70 text-[10px] md:text-xs italic font-serif-display mt-0.5">
        {stat.sub}
      </div>
    </div>
  );
}
